import { twMerge } from 'tailwind-merge';

/* ============================================================
   =============== NAVBAR TYPOGRAPHY ==========================
   - Digunakan pada area navigasi atas.
   - Fokus pada keterbacaan cepat, ukuran lebih kecil dari hero.
   - Tidak digunakan di konten section biasa!
   ============================================================ */

// LOGO — brand identity utama, visual tebal & lebih besar dari menu
export function Logo({ children, className }) {
  return (
    <span className={twMerge('text-base sm:text-lg md:text-xl font-semibold', className)}>
      {children}
    </span>
  );
}

// MENU — link navigasi utama (hover berubah warna)
export function Menu({ children, className }) {
  return (
    <span className={twMerge('text-sm sm:text-sm md:text-base font-semibold', className)}>
      {children}
    </span>
  );
}

export function MenuCategory({ children, className }) {
  return (
    <span className={twMerge('text-sm sm:text-base font-semibold tracking-wide', className)}>
      {children}
    </span>
  );
}

// Dropdown / Submenu item (lebih kecil)
export function MenuSub({ children, className }) {
  return <span className={twMerge('text-xs sm:text-sm font-normal', className)}>{children}</span>;
}

// MENU BTN — tombol aksi di navbar (Login/Daftar dll)
export function MenuButton({ children, className }) {
  return (
    <span className={twMerge('text-sm sm:text-base font-semibold', className)}>{children}</span>
  );
}

/* ============================================================
   ===================== HERO SECTION =========================
   - Ukuran paling besar dalam sistem tipografi.
   - Menarik perhatian utama di halaman landing/banner.
   ============================================================ */

// H1 — Judul paling besar untuk Hero/Header utama website
export function H1({ children, className }) {
  return (
    <h1
      className={twMerge(
        'text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight',
        className
      )}
    >
      {children}
    </h1>
  );
}

// HeroSub — Subheadline pendukung H1 di Hero section
export function HeroSub({ children, className }) {
  return (
    <p
      className={twMerge(
        'text-sm sm:text-base md:text-lg leading-relaxed text-gray-600',
        className
      )}
    >
      {children}
    </p>
  );
}

/* ============================================================
   ======================= TITLES =============================
   - Digunakan untuk heading section (About, Services, Contact).
   - Tidak digunakan untuk Hero atau Card title kecil.
   ============================================================ */

// H2 — Judul besar section (misal: Tentang Kami, Layanan)
export function H2({ children, className }) {
  return (
    <h2
      className={twMerge('text-2xl sm:text-3xl md:text-4xl font-semibold leading-snug', className)}
    >
      {children}
    </h2>
  );
}

// H3 — Subjudul section atau judul card besar
export function H3({ children, className }) {
  return (
    <h3 className={twMerge('text-lg sm:text-xl md:text-2xl font-semibold leading-snug', className)}>
      {children}
    </h3>
  );
}
export function H4({ children, className }) {
  return (
    <h4
      className={twMerge('text-sm sm:text-base xl:text-lg font-semibold leading-snug', className)}
    >
      {children}
    </h4>
  );
}
export function H5({ children, className }) {
  return (
    <h5
      className={twMerge('text-xs sm:text-sm xl:text-base font-semibold leading-snug', className)}
    >
      {children}
    </h5>
  );
}

/* ============================================================
   ===================== BODY TEXT ============================
   - Dipakai untuk paragraf konten, deskripsi produk, artikel.
   - Dipilih berdasarkan kepadatan teks & perangkat (responsive).
   ============================================================ */

// BodyLg — teks paragraf utama (konten utama tiap section)
export function BodyLg({ children, className }) {
  return <p className={twMerge('text-sm sm:text-base leading-relaxed', className)}>{children}</p>;
}

// Body — paragraf standar (deskripsi fitur, teks card)
export function Body({ children, className }) {
  return (
    <p className={twMerge('text-xs sm:text-sm md:text-base leading-relaxed', className)}>
      {children}
    </p>
  );
}

// BodySm — teks kecil (tooltip, informasi detail, card kecil)
export function BodySm({ children, className }) {
  return <p className={twMerge('text-xs md:text-sm leading-relaxed', className)}>{children}</p>;
}
export function BodySmHTML({ content, className }) {
  return (
    <p
      className={twMerge('text-xs xl:text-sm leading-relaxed', className)}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
}
/* ============================================================
   ======================== CAPTION ===========================
   - Informasi mikro (label kecil, tag status, tahun copyright, dsb)
   - Tidak cocok untuk paragraf panjang.
   ============================================================ */

export function Caption({ children, className }) {
  return (
    <span className={twMerge('text-[10px] sm:text-xs leading-none', className)}>{children}</span>
  );
}

/* ============================================================
   ======================= BUTTON TEXT ========================
   - Khusus teks pada tombol (bukan body biasa).
   - Tidak digunakan untuk link atau menu.
   ============================================================ */

export function BtnText({ children, className }) {
  return (
    <span className={twMerge('text-sm md:text-base font-semibold', className)}>{children}</span>
  );
}

// BtnTextSm — tombol kecil (card, pagination, slider, mobile)
export function BtnTextSm({ children, className }) {
  return <span className={twMerge('text-xs sm:text-sm font-semibold', className)}>{children}</span>;
}

/* ============================================================
   ======================= FOOTER TEXT ========================
   - Warna & ukuran dibuat lebih kecil karena fokus sekunder.
   - Memberi struktur pada bagian footer.
   ============================================================ */

export function FooterTitle({ children, className }) {
  return <h4 className={twMerge('text-base sm:text-lg font-semibold', className)}>{children}</h4>;
}

export function FooterBody({ children, className }) {
  return <p className={twMerge('text-xs sm:text-sm leading-relaxed', className)}>{children}</p>;
}

export function FooterCopy({ children, className }) {
  return (
    <span className={twMerge('text-[10px] sm:text-xs opacity-80', className)}>{children}</span>
  );
}

// Untuk Ckeditor
export function BodyLgHtml({ html, className }) {
  return (
    <div
      className={twMerge(
        'prose prose-lg max-w-none ' +
          'prose-img:rounded-xl prose-img:shadow-lg prose-img:w-full prose-img:h-auto ' +
          'prose-p:leading-relaxed prose-headings:font-semibold',
        className
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
